/**
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプライン一覧のフィルタリング純粋ロジック層。URL search params の Zod スキーマと検索マッチ判定を提供
 * @graph-connects none
 */

import { z } from 'zod';
import { PIPELINE_TYPE_VALUES, type PipelineConfig } from '../types/api.js';

/**
 * URL search params の Zod スキーマ。リロード / 共有 URL でフィルタ状態を
 * 保持できるよう、`type` / `status` / `search` をクエリに乗せる。
 * 不正値は `.catch(...)` で安全な既定値にフォールバックする。
 * `type` の候補は `PIPELINE_TYPE_VALUES`（SoT）を参照し、種別追加時の二重メンテを防ぐ。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business パイプライン一覧の URL search params スキーマ。type/status/search を検証し不正値を既定値にフォールバック
 * @graph-connects none
 */
export const pipelineSearchSchema = z.object({
  type: z
    .enum(['all', ...PIPELINE_TYPE_VALUES])
    .catch('all')
    .default('all'),
  status: z.enum(['all', 'ACTIVE', 'DISABLED']).catch('all').default('all'),
  search: z.string().catch('').default(''),
});

/**
 * 検索クエリが pipeline の人間可読フィールドのいずれかにヒットするか判定する。
 * jobName だけだと createdBy や targetDb で探したいケースを取りこぼすため、
 * pipeline 一覧で運用上引き合いに出される 3 フィールドを横断する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business 検索クエリがジョブ名・作成者・対象DBのいずれかに大文字小文字無視でヒットするか判定
 * @graph-connects none
 */
export function matchesSearch(config: PipelineConfig, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  const haystacks = [config.jobName, config.createdBy, config.targetDb ?? ''];
  return haystacks.some((value) => value.toLowerCase().includes(needle));
}
